import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getMyAccount, updateMyProfile } from "@/lib/account.functions";
import { getMyReferralStatus } from "@/lib/referrals.functions";
import { getMixologistTrackStatus } from "@/lib/mixologist.functions";
import { exportMyData } from "@/lib/compliance.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { PayoutScheduleCard } from "@/components/PayoutScheduleCard";

import { VerifiedMixologistBadge } from "@/components/VerifiedMixologistBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { buildEmailConfirmationRedirect } from "@/lib/signup-resume";

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Account Settings — The Hospitality Reserve" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountPage,
});

function StatusRow({
  label,
  ok,
  pending,
  detail,
  action,
}: {
  label: string;
  ok: boolean;
  pending?: boolean;
  detail: string;
  action?: React.ReactNode;
}) {
  const tone = ok
    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
    : pending
      ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30"
      : "bg-muted text-muted-foreground border-border";
  const badge = ok ? "Complete" : pending ? "Pending review" : "Action needed";
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-wide-luxury ${tone}`}>
          {badge}
        </span>
        {action}
      </div>
    </li>
  );
}


function AccountPage() {
  const navigate = useNavigate();
  const load = useServerFn(getMyAccount);
  const save = useServerFn(updateMyProfile);
  const exportData = useServerFn(exportMyData);
  const loadReferral = useServerFn(getMyReferralStatus);
  const [referral, setReferral] = useState<{
    code: string | null;
    pending_count: number;
    rewarded_count: number;
    venue_fee_waivers_remaining: number;
    history?: Array<{
      id: string;
      kind: string;
      status: string;
      created_at: string;
      rewarded_at: string | null;
      reward_granted: boolean;
      referred_name: string;
    }>;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await exportData({ data: {} as never });
      const blob = new Blob([r.json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reserve-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not export your data.");
    } finally {
      setExporting(false);
    }
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [headline, setHeadline] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<string>("");
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [emailConfirmedAt, setEmailConfirmedAt] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [hasDbs, setHasDbs] = useState(false);
  const [rtwStatus, setRtwStatus] = useState<string | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [mixoStatus, setMixoStatus] = useState<string | null>(null);
  const [mixoReviewedAt, setMixoReviewedAt] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const loadMixo = useServerFn(getMixologistTrackStatus);
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const me = await load();
      setEmail(me.email ?? "");
      setEmailConfirmed(Boolean(me.emailConfirmed));
      setEmailConfirmedAt(me.emailConfirmedAt ?? null);
      const p = me.profile as {
        full_name: string | null;
        headline: string | null;
        phone_number: string | null;
        role: string | null;
        onboarding_status: string | null;
        is_verified: boolean | null;
        has_dbs: boolean | null;
        rtw_status: string | null;
      } | null;
      setFullName(p?.full_name ?? "");
      setHeadline(p?.headline ?? "");
      setPhone(p?.phone_number ?? "");
      setRole(me.roles[0] ?? p?.role ?? "");
      setIsVerified(Boolean(p?.is_verified));
      setHasDbs(Boolean(p?.has_dbs));
      setRtwStatus(p?.rtw_status ?? null);
      setOnboardingStatus(p?.onboarding_status ?? null);
      setLoading(false);
      try {
        const r = await loadReferral();
        setReferral({
          code: r.code,
          pending_count: r.pending_count,
          rewarded_count: r.rewarded_count,
          venue_fee_waivers_remaining: r.venue_fee_waivers_remaining,
        });
      } catch { /* non-blocking */ }
      try {
        const m = await loadMixo();
        if ("ok" in m && m.profile) {
          setMixoStatus((m.profile as { mixologist_track_status: string | null }).mixologist_track_status ?? null);
          setMixoReviewedAt((m.profile as { mixologist_reviewed_at: string | null }).mixologist_reviewed_at ?? null);
        }
      } catch { /* non-blocking */ }
    })();
  }, [load, loadReferral, loadMixo]);

  const handleResendConfirmation = async () => {
    if (!email) return;
    setResendingEmail(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: buildEmailConfirmationRedirect(window.location.origin, "/dashboard"),
        },
      });
      if (error) throw error;
      toast.success("Confirmation email sent. Check your inbox (and spam).");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send the email.");
    } finally {
      setResendingEmail(false);
    }
  };


  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await save({ data: { full_name: fullName, headline, phone_number: phone } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setEmailBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      toast.success("Check both inboxes to confirm the new address.");
      setNewEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update email.");
    } finally {
      setEmailBusy(false);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd1.length < 8) { toast.error("Use at least 8 characters."); return; }
    if (pwd1 !== pwd2) { toast.error("Passwords don't match."); return; }
    setPwdBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd1 });
      if (error) throw error;
      toast.success("Password updated.");
      setPwd1(""); setPwd2("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update password.");
    } finally {
      setPwdBusy(false);
    }
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ReserveHeader />
        <main className="mx-auto max-w-2xl px-6 py-10">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-2xl px-6 py-10 space-y-12">
        <header>
          <p className="tracking-wide-luxury text-accent">Account Settings</p>
          <h1 className="mt-3 font-display text-3xl">Your account</h1>
          <span className="gold-divider mt-4" />
          <p className="mt-4 text-sm text-muted-foreground">
            Update your profile, change your sign-in credentials, or close your account.
          </p>
        </header>

        {/* Onboarding status */}
        <section className="border border-border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl">Onboarding status</h2>
            <VerifiedMixologistBadge status={mixoStatus} reviewedAt={mixoReviewedAt} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            These checks unlock parts of the marketplace. Verified bartenders see all
            open shifts; DBS-cleared bartenders unlock private and child-facing engagements.
          </p>

          <ul className="mt-5 divide-y divide-border border border-border">
            <StatusRow
              label="Email confirmed"
              ok={emailConfirmed}
              detail={
                emailConfirmed
                  ? emailConfirmedAt
                    ? `Confirmed ${new Date(emailConfirmedAt).toLocaleDateString()}`
                    : "Confirmed"
                  : "Required to access the marketplace."
              }
              action={
                !emailConfirmed && email ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={resendingEmail}
                    onClick={handleResendConfirmation}
                    className="rounded-none tracking-wide-luxury"
                  >
                    {resendingEmail ? "Sending…" : "Resend email"}
                  </Button>
                ) : null
              }
            />
            {role === "bartender" && (
              <>
                <StatusRow
                  label="Identity verified"
                  ok={isVerified && rtwStatus === "verified"}
                  pending={!isVerified && rtwStatus === "submitted"}
                  detail={
                    isVerified && rtwStatus === "verified"
                      ? "Right to work documents on file."
                      : rtwStatus === "submitted"
                        ? "Submitted — awaiting review."
                        : rtwStatus === "rejected"
                          ? "Rejected — please resubmit."
                          : "Upload your Right to Work documents to verify."
                  }
                  action={
                    !(isVerified && rtwStatus === "verified") ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => navigate({ to: "/bartender/onboarding" })}
                        className="rounded-none tracking-wide-luxury"
                      >
                        {rtwStatus === "verified" ? "View" : "Continue"}
                      </Button>
                    ) : null
                  }
                />
                <StatusRow
                  label="DBS check on file"
                  ok={hasDbs}
                  detail={
                    hasDbs
                      ? "You can apply to DBS-required shifts."
                      : "Optional — unlocks DBS-required shifts (private events, child-facing venues)."
                  }
                />
              </>
            )}
          </ul>

          {role === "bartender" && onboardingStatus && onboardingStatus !== "approved" && (
            <p className="mt-4 text-xs text-muted-foreground">
              Current application stage:{" "}
              <span className="text-foreground">{onboardingStatus.replace(/_/g, " ")}</span>
            </p>
          )}
        </section>

        {/* Referrals */}
        {referral?.code && (
          <section className="border border-accent/40 bg-accent/5 p-6">
            <p className="tracking-wide-luxury text-accent text-xs">Invite & earn</p>
            <h2 className="mt-2 font-display text-xl">Your referral code</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {role === "venue"
                ? "Refer another venue. When their first shift completes, you (the referrer) receive a platform-fee waiver on a future shift of up to 5 workers. The venue you refer does not receive a reward — only you do."
                : "Refer another bartender. When they complete their first shift, you (the referrer) receive £10 paid out via Stripe. The bartender you refer does not receive a cash bonus — only you do."}
            </p>
            <p className="mt-2 text-[11px] tracking-wide-luxury uppercase text-muted-foreground">
              Rewards are paid to the referrer only.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
              <div className="border border-border bg-background px-4 py-3">
                <p className="text-[10px] tracking-wide-luxury uppercase text-muted-foreground">Code</p>
                <p className="font-mono text-2xl tracking-widest text-foreground">{referral.code}</p>
              </div>
              <div className="flex flex-col gap-2 justify-center">
                <Button
                  type="button"
                  onClick={async () => {
                    const url = `${window.location.origin}/auth?mode=signup&ref=${referral.code}`;
                    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Referral link copied."); }
                    catch { toast.error("Could not copy. Try long-pressing the link."); }
                  }}
                  className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
                >
                  {copied ? "Copied ✓" : "Copy invite link"}
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(referral.code ?? ""); toast.success("Code copied."); }
                    catch { /* ignore */ }
                  }}
                  className="rounded-none border border-border bg-transparent text-foreground hover:bg-foreground hover:text-background tracking-wide-luxury"
                >
                  Copy code only
                </Button>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-3 gap-3 text-center border-t border-accent/30 pt-4">
              <div>
                <dt className="text-[10px] tracking-wide-luxury uppercase text-muted-foreground">Pending</dt>
                <dd className="mt-1 font-display text-2xl">{referral.pending_count}</dd>
              </div>
              <div>
                <dt className="text-[10px] tracking-wide-luxury uppercase text-muted-foreground">Rewarded</dt>
                <dd className="mt-1 font-display text-2xl">{referral.rewarded_count}</dd>
              </div>
              <div>
                <dt className="text-[10px] tracking-wide-luxury uppercase text-muted-foreground">
                  {role === "venue" ? "Waivers left" : "£ earned"}
                </dt>
                <dd className="mt-1 font-display text-2xl">
                  {role === "venue" ? referral.venue_fee_waivers_remaining : `£${referral.rewarded_count * 10}`}
                </dd>
              </div>
            </dl>

            {/* Referral history */}
            <div className="mt-6 border-t border-accent/30 pt-4">
              <h3 className="font-display text-sm tracking-wide-luxury uppercase text-muted-foreground">
                Referral history
              </h3>
              {!referral.history || referral.history.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  No referrals yet. Share your code or invite link to get started.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-accent/20 border border-accent/20 bg-background">
                  {referral.history.map((h) => {
                    const dateStr = new Date(h.created_at).toLocaleDateString("en-GB", {
                      day: "numeric", month: "short", year: "numeric",
                    });
                    const rewardLabel = h.kind === "venue" ? "Fee waiver granted" : "£10 paid to you";
                    return (
                      <li key={h.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                        <div className="min-w-0">
                          <p className="truncate text-foreground">{h.referred_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {h.kind === "venue" ? "Venue" : "Bartender"} · joined {dateStr}
                          </p>
                        </div>
                        <div className="text-right">
                          {h.reward_granted ? (
                            <>
                              <p className="text-xs font-medium text-foreground">First shift completed</p>
                              <p className="text-[11px] text-accent">{rewardLabel}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-xs text-muted-foreground">Awaiting first completed shift</p>
                              <p className="text-[11px] text-muted-foreground">Reward pending</p>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        )}




        {/* Profile */}
        <section className="border border-border bg-card p-6">
          <h2 className="font-display text-xl">Profile details</h2>
          <form onSubmit={handleSave} className="mt-4 space-y-4">
            <div>
              <Label htmlFor="fullName">Full name</Label>
              <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1.5" />
            </div>
            {role === "venue" && (
              <div>
                <Label htmlFor="headline">Venue / company name</Label>
                <Input id="headline" value={headline} onChange={(e) => setHeadline(e.target.value)} className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={saving} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </form>
        </section>

        {/* Email */}
        <section className="border border-border bg-card p-6">
          <h2 className="font-display text-xl">Email address</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Receipts, reminders, and dispute updates are sent to: <span className="text-foreground">{email}</span>
          </p>
          <form onSubmit={handleEmail} className="mt-4 space-y-3">
            <Label htmlFor="newEmail">New email</Label>
            <Input id="newEmail" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@example.com" />
            <Button type="submit" disabled={emailBusy || !newEmail} className="rounded-none border border-border bg-transparent text-foreground hover:bg-foreground hover:text-background tracking-wide-luxury">
              {emailBusy ? "Sending…" : "Send confirmation"}
            </Button>
          </form>
          <p className="mt-4 text-xs text-muted-foreground">
            Choose which receipts, reminders, and dispute alerts you receive by email, SMS, or in-app.
          </p>
          <Button
            type="button"
            onClick={() => navigate({ to: "/notification-settings" })}
            className="mt-3 rounded-none border border-border bg-transparent text-foreground hover:bg-foreground hover:text-background tracking-wide-luxury"
          >
            Manage notification preferences →
          </Button>
        </section>

        {/* Password */}
        <section className="border border-border bg-card p-6">
          <h2 className="font-display text-xl">Password</h2>
          <form onSubmit={handlePassword} className="mt-4 space-y-3">
            <div>
              <Label htmlFor="pwd1">New password</Label>
              <PasswordInput id="pwd1" minLength={8} autoComplete="new-password" value={pwd1} onChange={(e) => setPwd1(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="pwd2">Confirm new password</Label>
              <PasswordInput id="pwd2" minLength={8} autoComplete="new-password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={pwdBusy || !pwd1} className="rounded-none border border-border bg-transparent text-foreground hover:bg-foreground hover:text-background tracking-wide-luxury">
              {pwdBusy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </section>

        <PayoutScheduleCard />

        {/* Your data (GDPR) */}

        <section className="border border-border bg-card p-6">
          <h2 className="font-display text-xl">Your data</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Download everything we hold about you — profile, bookings, payouts, messages,
            reviews, consent history, and Right-to-Work audit trail — as a JSON file.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="mt-4 rounded-none border border-border bg-transparent text-foreground hover:bg-foreground hover:text-background tracking-wide-luxury"
          >
            {exporting ? "Preparing…" : "Download my data"}
          </Button>
        </section>

        {/* Danger zone */}
        <section className="border border-destructive/40 bg-destructive/5 p-6">
          <h2 className="font-display text-xl text-destructive">Delete account</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Submit a verified deletion request under GDPR Article 17. We will email you to confirm,
            then apply a 7-day cooling-off period before permanently scrubbing your personal data.
            Financial records are retained for 6 years for HMRC compliance.
          </p>
          <Link
            to="/account/delete"
            className="mt-4 inline-block border border-destructive bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury px-6 py-3 text-xs"
          >
            Begin deletion request
          </Link>
          <p className="mt-4 text-xs text-muted-foreground">
            See our full <Link to="/account/privacy" className="underline">Privacy &amp; Data centre</Link> for export, deletion, and cookie controls, or manage your{" "}
            <Link to="/account/security" className="underline">signed-in devices</Link>.
          </p>
        </section>

      </main>
    </div>
  );
}
